/**
 * 광고 연동 문서 : {@link https://naver.github.io/glad-sdk-guide/ko/web_new/common/get_started/}
 */

interface AdDeduplicationManager {
  displayAds(adSlots: AdSlot[]): void
  restoreAds(adSlots?: AdSlot[]): void
}

export interface GladSdk {
  defineAdSlot: (adSlotInfo: AdSlotInfo) => AdSlot
  displayAd: (divOrAdSlot: string | Element | AdSlot) => void
  findAdSlot: (adSlotElementId: string) => AdSlot
  destroyAdSlots: (adSlots?: AdSlot[]) => void
  refreshAdSlots: (adSlots: AdSlot[]) => void
  setGlobalConfig: (sdkConfig: GladSdkConfig) => void
  findAdDedupManager(dedupReqId: string): AdDeduplicationManager | null
  /**
   * @param dedupReqId 중복 제어 매니저를 식별하기 위한 ID
   * @param maxDedupReqCount 중복 제어를 적용할 광고 슬롯의 개수 - 1
   * @returns 생성된 중복 제어 매니저. dedupReqId로 생성된 매니저가 있을 경우 생성된 매니저를 반환합니다.
   */
  setAdDeduplication(dedupReqId: string, maxDedupReqCount: number): AdDeduplicationManager
  clearAdDeduplication(dedupReqId: string): void
  setHostMeta: <K extends keyof MetaKeyValueMap>(key: K, value: MetaKeyValueMap[K]) => void
  removeAllEventListener: () => void
  addEventListener: (event: string, listener: GladSdkListener) => void
  removeEventListener: (event: string, listener: GladSdkListener) => void
  event: GladSdkEvent
  cmd: (() => void)[]
}

interface MetaKeyValueMap {
  theme: 'dark' | 'light' | 'system'
}

export interface AdSlot {
  [key: string]: unknown
}

export interface GladSdkError {
  errorCode: number // 3310
  errorMessage: string // 'Empty render type.'
  errorSubCode: string // 'GFP_NO_FILL'
  stat: unknown | null // null
}

export interface Advertisement {
  slot: {
    getAdUnitId: () => string
    getAdSlotElementId: () => string
  }
}

export type GladSdkListener = (data: Advertisement, error?: GladSdkError) => void

export enum AdChannel {
  WEB = 'WEB',
  MAP_IOS = 'MAP_IOS',
  MAP_AOS = 'MAP_AOS',
}

export interface GladSdkMyPlaceCustomParams {
  userIdno?: string | undefined
  channel: AdChannel
}

export interface AdSlotInfo {
  adUnitId: string
  adSlotElementId: string
  ai?: string // ad id (optional)
  iv?: string // identifierForVendor (optional)
  yob?: string // year of birth (optional), 4 digit number
  gender?: string // gender (optional), 'M':male, 'F': female, 'O': unknown
  uct?: string // user country (optional)
  uid?: string // publisher user identifier (optional)
  customParam?: GladSdkMyPlaceCustomParams // custom parameter (optional)
}

export interface GladSdkConfig {
  requestTimeout?: number //  전체 요청 타임 아웃값(number), 기본값 60000ms
  enablePersistAd?: boolean //  광고 퍼시스트 설정 여부(boolean), 기본값 false
  debug?: boolean // 디버그 모드 설정(boolean), 기본값 false, phase 설정 시에만 true 로 설정
  language?: 'ko' | 'en' | 'ja' | 'zh-tw' | 'th' | 'id' //  언어 코드값, ko(Korean), en(English), ja(Japanese), zh-tw(Taiwanese), th(Thai), id(Indonesian), 네이티브 광고 설정 시 필요, 기본값 en
  phase?: 'dev' | 'test' | 'staging' | 'production' //  phase 설정, 'dev', 'test', 'staging', 'production'
  appName?: string //  서비스 이름, 기본값은 도메인 네임(m.naver.com)
  appVersion?: string // 서비스 릴리즈 버전
  maxDedupReqCount?: number //  광고 중복 제어 결과값 유지 횟수(number)
  dependentService?: unknown //  dependent service 정보, 네이티브 광고의 라인 기본 클릭 핸들러 설정이 필요한 경우 설정(serviceName: 서비스명, scheme : 라인앱 scheme)
}

export interface GladSdkEvent {
  AD_LOADED: 'AD_LOADED' //	광고 슬롯에 광고 로드 시 발생
  ERROR: 'ERROR' //	광고 로드 실패 혹은 실행 에러 시 발생
  AD_IMPRESSED: 'AD_IMPRESSED' //	광고 슬롯에 랜더링된 소재 노출 기준 만족 시 발생
  AD_CLICKED: 'AD_CLICKED' //	광고 슬롯에 랜더링된 소재 클릭 시 발생
  AD_MUTE_COMPLETED: 'AD_MUTE_COMPLETED' //	광고 뮤트 사유 클릭 시 발생
}
